import { Dialog, IDialog, SELECTORS as DIALOG_SELECTORS } from "../Dialog";

export interface IToast extends IDialog {
    title: string;
    link: string;
    href: string;
    type: string;
    content: string;
    autoCloseTimeout: number;
}

export interface IToastOptions {
    title: string;
    content?: string;
    autoCloseTimeout?: number;
    link?: string;
    href?: string;
    type?: string;
}

export class Toast extends Dialog implements IToast {
    static TYPE_SUCCESS = "success";
    static TYPE_ERROR = "error";
    static TYPE_INFO = "info";

    protected templateId = "dialog-toast";

    protected modal = false;

    /**
     * set to zero to disable auto close
     */
    public autoCloseTimeout: number;

    constructor({
        title,
        content = "",
        link = "",
        href = "",
        autoCloseTimeout = 5000,
        type = Toast.TYPE_INFO
    }: IToastOptions) {
        super();
        if (!title) {
            throw new TypeError("title can not be empty");
        }
        this.title = title;
        this.content = content;
        this.link = link;
        this.href = href;
        this.type = type;
        this.autoCloseTimeout = autoCloseTimeout;
    }

    /**
     * @inheritDoc
     */
    public render(): IToast {
        const btnClose = this.dialog.querySelector(DIALOG_SELECTORS.CLOSE_ICON);
        if (this.autoCloseTimeout) {
            btnClose.remove();
            this.dialog.addEventListener(
                "transitionend",
                () => {
                    this.initAutoClose();
                },
                this.eventOpts
            );
        } else {
            btnClose.addEventListener("click", () => this.close(), this.eventOpts);
        }
        super.render();
        return this;
    }

    /**
     * initialize auto close
     * @private
     */
    private initAutoClose() {
        setTimeout(() => {
            this.close();
        }, this.autoCloseTimeout);
    }

    /**
     * test all options
     * @param autoClose
     */
    public static test(autoClose: boolean = true): IToast {
        return new Toast({
            title: window.SuccessMessages.cart_add.success,
            type: Toast.TYPE_SUCCESS,
            content:
                "Lorem ipsum dolor sit amet, consectetur adipisicing elit. A accusamus dignissimos enim excepturi quasi voluptates!",
            link: "Clickme",
            href: "https://google.de",
            autoCloseTimeout: autoClose ? 5000 : 0
        })?.render();
    }

    /**
     * test simple info message
     */
    public static testInfo(): IToast {
        return new Toast({
            title: "Toast Info",
            type: Toast.TYPE_SUCCESS
        })?.render();
    }

    /**
     * test simple success message
     */
    public static testSuccess(): IToast {
        return new Toast({
            title: window.SuccessMessages.cart_add.success,
            type: Toast.TYPE_SUCCESS
        })?.render();
    }

    /**
     * test simple warning
     */
    public static testError(): IToast {
        return new Toast({
            title: window.SuccessMessages.cart_add.error,
            type: Toast.TYPE_ERROR
        })?.render();
    }

    /**
     * set title
     * @param {String} v
     */
    public set title(v: string) {
        this.dialog.querySelector<HTMLSpanElement>(DIALOG_SELECTORS.TITLE).innerText = v;
    }

    /**
     * @inheritdoc
     */
    public set content(v: string) {
        this.dialog.querySelector<HTMLSpanElement>(DIALOG_SELECTORS.MESSAGE).innerText = v;
    }

    /**
     * set link text
     * @param {String} v
     */
    public set link(v: string) {
        this.dialog.querySelector<HTMLAnchorElement>(DIALOG_SELECTORS.LINK).innerText = v;
    }

    /**
     * set link href
     * @param {String} v
     */
    public set href(v: string) {
        this.dialog.querySelector<HTMLAnchorElement>(DIALOG_SELECTORS.LINK).href = v;
    }

    /**
     * obtain type
     * @returns {String}
     */
    public get type(): string {
        const classes = this.dialog.querySelector(DIALOG_SELECTORS.TYPE_ICON).classList;
        if (classes.contains(Toast.TYPE_INFO)) {
            return Toast.TYPE_INFO;
        }
        if (classes.contains(Toast.TYPE_SUCCESS)) {
            return Toast.TYPE_SUCCESS;
        }
        if (classes.contains(Toast.TYPE_ERROR)) {
            return Toast.TYPE_ERROR;
        }
    }

    /**
     * set type
     * @param {String} v
     */
    public set type(v: string) {
        let icon = Toast.TYPE_INFO;
        switch (v) {
            case Toast.TYPE_ERROR:
                icon = Toast.TYPE_ERROR;
                break;
            case Toast.TYPE_SUCCESS:
                icon = Toast.TYPE_SUCCESS;
                break;
        }
        this.dialog
            .querySelector(DIALOG_SELECTORS.TYPE_ICON)
            .classList.remove(Toast.TYPE_SUCCESS, Toast.TYPE_ERROR, Toast.TYPE_INFO);
        this.dialog.querySelector(DIALOG_SELECTORS.TYPE_ICON).classList.add(icon);
    }
}
